import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Button, Space, Input, Modal, Form, Select, InputNumber, message, Tag } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import request from '../../../utils/request';
import { getShippingPackagings, createShippingPackaging, updateShippingPackaging, deleteShippingPackaging, previewShippingPackagingLabel, downloadShippingPackagingLabel } from '../../../services/logistics';
import type { ShippingPackaging } from '../../../services/logistics';
import { getAllWarehouses } from '../../../services/warehouse';

type OwnershipType = 'warehouse' | 'customer';
type PackagingType = 'carton' | 'shipping_bag' | 'pallet' | 'bubble_mailer' | 'other';
type Condition = 'new' | 'used';
type ChargeType = 'charged' | 'free';

const ownershipLabel = (v?: string) => (v === 'warehouse' ? '仓库所属' : v === 'customer' ? '客户所属' : v || '-');
const typeLabel = (v?: string) => (
  v === 'carton' ? '纸箱'
  : v === 'shipping_bag' ? '物流袋'
  : v === 'pallet' ? '托盘'
  : v === 'bubble_mailer' ? '气泡信封袋'
  : v === 'other' ? '其他'
  : v || '-'
);
const typeLabelFull = (v?: string) => (
  v === 'pallet' ? '托盘/Pallet(P)'
  : v === 'carton' ? '纸箱/Carton(C)'
  : v === 'shipping_bag' ? '物流袋/Shipping Bag(SB)'
  : v === 'bubble_mailer' ? '气泡信封袋/Bubble Mailer(BM)'
  : v === 'other' ? '其他/Other(O)'
  : v || '-'
);
const condLabel = (v?: string) => (v === 'new' ? '全新' : v === 'used' ? '旧' : v || '-');

const packagingTagStyle = (t?: string): React.CSSProperties => {
  const key = String(t || '');
  const map: Record<string, { bg: string; bd: string; fg: string }> = {
    pallet: { bg: '#f6ffed', bd: '#b7eb8f', fg: '#389e0d' },
    carton: { bg: '#e6f4ff', bd: '#91caff', fg: '#0958d9' },
    shipping_bag: { bg: '#fff7e6', bd: '#ffd591', fg: '#d46b08' },
    bubble_mailer: { bg: '#f9f0ff', bd: '#d3adf7', fg: '#531dab' },
    other: { bg: '#f5f5f5', bd: '#d9d9d9', fg: '#595959' },
  };
  const c = map[key] || map.other;
  return { background: c.bg, border: `1px solid ${c.bd}`, color: c.fg };
};

const toNum = (v: any): number | undefined => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
const formatVol = (v: number) => {
  try {
    if (!Number.isFinite(v)) return '-';
    const rounded = Math.round(v * 100) / 100;
    // 避免显示 1.00 这类冗余
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0+$/,'').replace(/\.$/,'');
  } catch { return '-'; }
};

const ShippingPackagingListPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ShippingPackaging[]>([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState<TablePaginationConfig>({ current: 1, pageSize: 10, total: 0 });
  // 列表筛选
  const [fltOwnership, setFltOwnership] = useState<OwnershipType | undefined>(undefined);
  const [fltPackagingType, setFltPackagingType] = useState<PackagingType | undefined>(undefined);
  const [fltCondition, setFltCondition] = useState<Condition | undefined>(undefined);
  const [fltCustomerId, setFltCustomerId] = useState<number | undefined>(undefined);
  const [fltIsActive, setFltIsActive] = useState<boolean | undefined>(undefined);
  const [fltChargeType, setFltChargeType] = useState<ChargeType | undefined>(undefined);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [submitting, setSubmitting] = useState(false);
  const [editingRow, setEditingRow] = useState<ShippingPackaging | null>(null);
  const [form] = Form.useForm();
  // “包材新旧”默认值的自动设置：仅在用户未手动修改时生效
  const conditionAutoRef = useRef<string | undefined>(undefined);

  // 打印：复用现有体系（后端生成 PDF 标签，前端仅预览/打印/下载）
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelUrl, setLabelUrl] = useState<string>('');
  const [labelRow, setLabelRow] = useState<ShippingPackaging | null>(null);
  const labelIframeRef = useRef<HTMLIFrameElement | null>(null);

  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [optsLoading, setOptsLoading] = useState(false);

  const fetchOptions = async (): Promise<{ defaultWarehouseId?: number }> => {
    setOptsLoading(true);
    let defaultWarehouseId: number | undefined = undefined;
    try {
      // 与指令列表页一致：统一从 /api/v1/warehouses/ 获取（getAllWarehouses）
      const whList: any[] = await getAllWarehouses();
      const mapped = (whList || []).map((w:any) => {
        const code = String(w.system_code || w.code || w.warehouse_code || w.id || '').trim();
        const name = String(w.name || '').trim();
        const label = name ? `${code} - ${name}` : code;
        return { value: Number(w.id), label: label || String(w.id) };
      });
      setWarehouseOptions(mapped);
      try {
        // 默认仓库：US-1-NH 安飞物流美东仓库
        const hit = mapped.find((o) => String(o.label || '').includes('US-1-NH') && String(o.label || '').includes('安飞物流美东仓库'));
        if (hit && Number.isFinite(Number(hit.value))) defaultWarehouseId = Number(hit.value);
      } catch {}
    } catch {
      setWarehouseOptions([]);
    }
    try {
      // 仅显示客户主账号（避免子账号）
      const cu = await request.get('/api/v1/users/', { params: { role: 'customer', page_size: 200, customer_sub_role: 'main_account', sub_role: 'main_account' } });
      const cuList: any[] = Array.isArray(cu?.data?.results) ? cu.data.results : (Array.isArray(cu?.data) ? cu.data : []);
      setCustomerOptions((cuList || []).map((u:any) => ({ value: Number(u.id), label: String(u.username || u.company_name || u.id) })));
    } catch {
      setCustomerOptions([]);
    }
    setOptsLoading(false);
    return { defaultWarehouseId };
  };

  const fetchData = async (
    p: TablePaginationConfig,
    q?: string,
    overrides?: Partial<{ ownership_type?: OwnershipType; packaging_type?: PackagingType; condition?: Condition; customer?: number; is_active?: boolean; charge_type?: ChargeType }>
  ) => {
    setLoading(true);
    try {
      const current = Number(p?.current || 1) || 1;
      const pageSize = Number(p?.pageSize || 10) || 10;
      const ownership_type = (overrides && 'ownership_type' in overrides) ? overrides.ownership_type : fltOwnership;
      const packaging_type = (overrides && 'packaging_type' in overrides) ? overrides.packaging_type : fltPackagingType;
      const condition = (overrides && 'condition' in overrides) ? overrides.condition : fltCondition;
      const customer = (overrides && 'customer' in overrides) ? overrides.customer : fltCustomerId;
      const is_active = (overrides && 'is_active' in overrides) ? overrides.is_active : fltIsActive;
      const charge_type = (overrides && 'charge_type' in overrides) ? overrides.charge_type : fltChargeType;
      const resp = await getShippingPackagings({
        page: current,
        page_size: pageSize,
        search: q || undefined,
        ownership_type: ownership_type || undefined,
        packaging_type: packaging_type || undefined,
        condition: condition || undefined,
        customer: (customer != null ? Number(customer) : undefined),
        is_active: (typeof is_active === 'boolean' ? is_active : undefined),
        charge_type: charge_type || undefined,
      });
      const body = resp.data as any;
      const rows: ShippingPackaging[] = Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []);
      const total = Number(body?.count ?? rows.length) || 0;
      setData(rows);
      setPagination(prev => ({ ...prev, current, pageSize, total }));
    } catch (e) {
      message.error('获取发货包材列表失败');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 首次进入：加载筛选项（客户主账号）并拉取列表
    (async () => {
      try { await fetchOptions(); } catch {}
      fetchData(pagination, searchText);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchData({ ...pagination, current: 1 }, searchText);
  };
  const handleResetFilters = () => {
    try {
      setFltOwnership(undefined);
      setFltPackagingType(undefined);
      setFltCondition(undefined);
      setFltCustomerId(undefined);
      setFltIsActive(undefined);
      setFltChargeType(undefined);
      setSearchText('');
      fetchData({ ...pagination, current: 1 }, undefined, {
        ownership_type: undefined,
        packaging_type: undefined,
        condition: undefined,
        customer: undefined,
        is_active: undefined,
        charge_type: undefined,
      });
    } catch {}
  };

  const columns: ColumnsType<ShippingPackaging> = useMemo(() => ([
    { title: '系统编码', dataIndex: 'system_code', width: 105, ellipsis: true, render: (v:any) => <span style={{ fontFamily:'monospace' }}>{v || '-'}</span> },
    { title: '名称', dataIndex: 'name', width: 140, ellipsis: true, render: (v:any) => v || '-' },
    { title: '是否可用', dataIndex: 'is_active', width: 80, render: (v:any) => (v === false ? <Tag color="red">不可用</Tag> : <Tag color="green">可用</Tag>) },
    { title: '包材类型', dataIndex: 'packaging_type', width: 90, render: (v:any) => <Tag style={packagingTagStyle(String(v || ''))}>{typeLabel(String(v || ''))}</Tag> },
    { title: '新旧', dataIndex: 'condition', width: 66, render: (v:any) => <Tag color={String(v)==='new'?'green':'gold'}>{condLabel(String(v||''))}</Tag> },
    { title: '归属', dataIndex: 'ownership_type', width: 80, render: (v:any) => <Tag color={String(v)==='warehouse'?'blue':'purple'}>{ownershipLabel(String(v||''))}</Tag> },
    { title: '仓库', dataIndex: 'warehouse_name', width: 140, ellipsis: true, render: (_:any, r:any) => r?.warehouse_name || (r?.warehouse ? `#${r.warehouse}` : '-') },
    { title: '客户', dataIndex: 'customer_name', width: 105, ellipsis: true, render: (_:any, r:any) => r?.customer_name || (r?.customer ? `#${r.customer}` : '-') },
    { title: '价格($)', dataIndex: 'price', width: 80, render: (_:any, r:any) => (r?.ownership_type === 'warehouse' ? (r?.price ?? 0) : '-') },
    { title: '尺寸(cm)', key: 'size', width: 130, render: (_:any, r:any) => {
      // 规则：0 视为“未填写”，不展示；例如 30×20×0 → 30×20
      const vals = [toNum(r?.length_cm), toNum(r?.width_cm), toNum(r?.height_cm)]
        .filter((x): x is number => x != null && Number.isFinite(x) && x > 0);
      if (vals.length === 0) return '-';
      if (vals.length === 1) return formatVol(vals[0]);
      if (vals.length === 2) return `${formatVol(vals[0])}×${formatVol(vals[1])}`;
      return `${formatVol(vals[0])}×${formatVol(vals[1])}×${formatVol(vals[2])}`;
    }},
    { title: '容积(cm³)', key: 'volume', width: 100, render: (_:any, r:any) => {
      const l = toNum(r?.length_cm);
      const w = toNum(r?.width_cm);
      const h = toNum(r?.height_cm);
      if (l == null || w == null || h == null) return '-';
      // 无效尺寸（缺失/非数/<=0）不计算容积，避免显示 0
      if (!(l > 0 && w > 0 && h > 0)) return '-';
      return formatVol(l * w * h);
    }},
    { title: '重量(g)', dataIndex: 'weight_g', width: 90, render: (v:any) => (v==null || v==='' ? '-' : v) },
    { title: '操作', key: 'ops', width: 270, render: (_:any, r:any) => {
      return (
        <Space size={6} wrap>
          <Button size="small" onClick={async () => {
            try {
              setLabelRow(r);
              setLabelOpen(true);
            } catch {}
          }}>打印</Button>
          <Button size="small" onClick={async () => {
            try {
              const { defaultWarehouseId } = await fetchOptions();
              setModalMode('edit');
              setEditingRow(r);
              setModalOpen(true);
              setTimeout(() => {
                try { form.resetFields(); } catch {}
                const own = r?.ownership_type || 'warehouse';
                const payload: any = {
                  ownership_type: own,
                  packaging_type: r?.packaging_type,
                  condition: r?.condition,
                  name: r?.name || '',
                  remarks: (r as any)?.remarks || '',
                  length_cm: toNum(r?.length_cm) ?? null,
                  width_cm: toNum(r?.width_cm) ?? null,
                  height_cm: toNum(r?.height_cm) ?? null,
                  weight_g: toNum(r?.weight_g) ?? null,
                  is_active: r?.is_active !== false,
                };
                if (own === 'warehouse') {
                  payload.warehouse = r?.warehouse ?? defaultWarehouseId ?? null;
                  payload.price = toNum(r?.price) ?? 0;
                  payload.customer = null;
                } else {
                  payload.customer = r?.customer ?? null;
                  payload.warehouse = null;
                  payload.price = null;
                }
                try { form.setFieldsValue(payload); } catch {}
              }, 0);
            } catch {}
          }}>编辑</Button>
          <Button
            size="small"
            style={r?.is_active === false ? { background: '#52c41a', borderColor: '#52c41a', color: '#fff' } : { background: '#fa541c', borderColor: '#fa541c', color: '#fff' }}
            onClick={async () => {
            try {
              const nextActive = r?.is_active === false;
              await updateShippingPackaging(Number(r.id), { is_active: nextActive });
              message.success(nextActive ? '已设为可用' : '已设为不可用');
              await fetchData(pagination, searchText);
            } catch (e:any) {
              message.error(e?.response?.data?.detail || '更新状态失败');
            }
          }}>
            {r?.is_active === false ? '设为可用' : '设为不可用'}
          </Button>
          <Button size="small" danger onClick={() => {
            Modal.confirm({
              title: '确认删除该包材？',
              content: `系统编码：${r?.system_code || r?.id}`,
              okText: '删除',
              okButtonProps: { danger: true },
              cancelText: '取消',
              onOk: async () => {
                try {
                  await deleteShippingPackaging(Number(r.id));
                  message.success('已删除');
                  // 若当前页删空，回退一页
                  const cur = Number(pagination.current || 1) || 1;
                  const ps = Number(pagination.pageSize || 10) || 10;
                  const rest = (data || []).length - 1;
                  const nextPage = (rest <= 0 && cur > 1) ? cur - 1 : cur;
                  await fetchData({ ...pagination, current: nextPage, pageSize: ps }, searchText);
                } catch (e:any) {
                  message.error(e?.response?.data?.detail || '删除失败');
                }
              }
            });
          }}>删除</Button>
        </Space>
      );
    }},
  ]), [data, pagination, searchText]);

  const ownershipVal = Form.useWatch('ownership_type', form) as OwnershipType | undefined;

  // 归属类型变化时：自动设置“包材新旧”默认值
  useEffect(() => {
    if (!modalOpen || modalMode !== 'create') return;
    const desired = ownershipVal === 'customer' ? 'new' : 'used';
    try {
      const cur = form.getFieldValue('condition');
      const touched = form.isFieldTouched('condition');
      if (!touched || cur == null || String(cur) === String(conditionAutoRef.current || '')) {
        form.setFieldsValue({ condition: desired });
        conditionAutoRef.current = desired;
      }
    } catch {}
  }, [modalOpen, modalMode, ownershipVal, form]);

  // 打印标签：打开时拉取后端预览 PDF
  useEffect(() => {
    (async () => {
      if (!labelOpen || !labelRow?.id) return;
      setLabelLoading(true);
      try {
        if (labelUrl) {
          try { URL.revokeObjectURL(labelUrl); } catch {}
          setLabelUrl('');
        }
        const resp = await previewShippingPackagingLabel(Number(labelRow.id));
        const blob = resp?.data as Blob;
        const url = URL.createObjectURL(blob);
        setLabelUrl(url);
      } catch (e:any) {
        message.error(e?.response?.data?.error || e?.message || '生成标签预览失败');
        setLabelUrl('');
      } finally {
        setLabelLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelOpen, labelRow?.id]);

  // 关闭时回收 blob url
  useEffect(() => {
    if (labelOpen) return;
    if (labelUrl) {
      try { URL.revokeObjectURL(labelUrl); } catch {}
      setLabelUrl('');
    }
  }, [labelOpen]);

  return (
    <div className="p-6">
      <Card
        headStyle={{ borderBottom: 'none' }}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span>发货包材管理</span>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  border: '1px solid #91caff',
                  background: '#e6f4ff',
                  color: '#0958d9',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                共 {Number(pagination?.total || 0)} 条记录
              </span>
            </div>
            <Button onClick={async () => {
              const { defaultWarehouseId } = await fetchOptions();
              setModalMode('create');
              setEditingRow(null);
              setModalOpen(true);
              setTimeout(() => {
                form.resetFields();
                // 默认所属仓库：US-1-NH 安飞物流美东仓库
                if (defaultWarehouseId) {
                  try { form.setFieldsValue({ warehouse: defaultWarehouseId }); } catch {}
                }
                // 按归属类型设置“包材新旧”默认值：仓库=旧，客户=全新
                try {
                  const own = form.getFieldValue('ownership_type');
                  const desired = String(own) === 'customer' ? 'new' : 'used';
                  form.setFieldsValue({ condition: desired });
                  conditionAutoRef.current = desired;
                } catch {}
              }, 0);
            }} type="primary">
              新建包材
            </Button>
          </div>
        )}
      >
        {/* 筛选行：放在标题行下一行 */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <Select
            style={{ width: 140 }}
            placeholder="所属类型"
            allowClear
            value={fltOwnership}
            options={[
              { value: 'warehouse', label: '仓库所属' },
              { value: 'customer', label: '客户所属' },
            ]}
            onChange={(v) => {
              const val = (v ? (String(v) as OwnershipType) : undefined);
              setFltOwnership(val);
              fetchData({ ...pagination, current: 1 }, searchText, { ownership_type: val });
            }}
          />
          <Select
            style={{ width: 180 }}
            placeholder="包材类型"
            allowClear
            value={fltPackagingType}
            options={[
              { value: 'pallet', label: typeLabelFull('pallet') },
              { value: 'carton', label: typeLabelFull('carton') },
              { value: 'shipping_bag', label: typeLabelFull('shipping_bag') },
              { value: 'bubble_mailer', label: typeLabelFull('bubble_mailer') },
              { value: 'other', label: typeLabelFull('other') },
            ]}
            onChange={(v) => {
              const val = (v ? (String(v) as PackagingType) : undefined);
              setFltPackagingType(val);
              fetchData({ ...pagination, current: 1 }, searchText, { packaging_type: val });
            }}
          />
          <Select
            style={{ width: 120 }}
            placeholder="新旧"
            allowClear
            value={fltCondition}
            options={[
              { value: 'new', label: '全新' },
              { value: 'used', label: '旧' },
            ]}
            onChange={(v) => {
              const val = (v ? (String(v) as Condition) : undefined);
              setFltCondition(val);
              fetchData({ ...pagination, current: 1 }, searchText, { condition: val });
            }}
          />
          <Select
            style={{ width: 120 }}
            placeholder="是否可用"
            allowClear
            value={typeof fltIsActive === 'boolean' ? String(fltIsActive) : undefined}
            options={[
              { value: 'true', label: '可用' },
              { value: 'false', label: '不可用' },
            ]}
            onChange={(v) => {
              const val = (v === 'true' ? true : v === 'false' ? false : undefined);
              setFltIsActive(val);
              fetchData({ ...pagination, current: 1 }, searchText, { is_active: val });
            }}
          />
          <Select
            style={{ width: 120 }}
            placeholder="是否收费"
            allowClear
            value={fltChargeType}
            options={[
              { value: 'charged', label: '收费' },
              { value: 'free', label: '免费' },
            ]}
            onChange={(v) => {
              const val = (v ? (String(v) as ChargeType) : undefined);
              setFltChargeType(val);
              fetchData({ ...pagination, current: 1 }, searchText, { charge_type: val });
            }}
          />
          <Select
            style={{ width: 200 }}
            placeholder="所属客户（主账号）"
            allowClear
            showSearch
            loading={optsLoading}
            value={fltCustomerId}
            options={customerOptions}
            optionFilterProp="label"
            filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
            onChange={(v) => {
              const val = (typeof v === 'number' && Number.isFinite(v)) ? v : undefined;
              setFltCustomerId(val);
              fetchData({ ...pagination, current: 1 }, searchText, { customer: val });
            }}
          />
          <Input
            placeholder="搜索系统编码/名称"
            style={{ width: 220 }}
            value={searchText}
            onChange={(e)=> setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
          />
          <Button onClick={handleSearch} type="primary">搜索</Button>
          <Button onClick={handleResetFilters}>重置</Button>
        </div>
        <Table<ShippingPackaging>
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          onChange={(p)=> fetchData(p, searchText)}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
          }}
        />
      </Card>

      <Modal
        title={modalMode === 'edit' ? '编辑发货包材' : '新建发货包材'}
        open={modalOpen}
        onCancel={() => { if (!submitting) setModalOpen(false); }}
        okText={modalMode === 'edit' ? '保存' : '创建'}
        cancelText="取消"
        confirmLoading={submitting}
        onOk={async () => {
          try {
            const values = await form.validateFields();
            setSubmitting(true);
            const payload: any = {
              ownership_type: values.ownership_type,
              packaging_type: values.packaging_type,
              condition: values.condition,
              name: values.name || '',
              remarks: values.remarks || '',
              length_cm: values.length_cm ?? null,
              width_cm: values.width_cm ?? null,
              height_cm: values.height_cm ?? null,
              weight_g: values.weight_g ?? null,
              is_active: values.is_active !== false,
            };
            if (values.ownership_type === 'warehouse') {
              payload.warehouse = values.warehouse;
              payload.price = values.price;
              payload.customer = null;
            } else {
              payload.customer = values.customer;
              payload.warehouse = null;
              payload.price = null;
            }
            if (modalMode === 'edit' && editingRow?.id) {
              await updateShippingPackaging(Number(editingRow.id), payload);
              message.success('保存成功');
            } else {
              await createShippingPackaging(payload);
              message.success('创建成功');
            }
            setModalOpen(false);
            setEditingRow(null);
            await fetchData({ ...pagination, current: 1 }, searchText);
          } catch (e:any) {
            if (e?.errorFields) return; // 表单校验错误
            const detail = e?.response?.data || e?.message || '创建失败';
            if (typeof detail === 'object') {
              Object.entries(detail).forEach(([k, v]) => message.error(`${k}: ${Array.isArray(v) ? v[0] : v}`));
            } else {
              message.error(String(detail));
            }
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ ownership_type: 'warehouse', packaging_type: 'carton', condition: 'used', price: 0 }}
        >
          <Form.Item label="归属类型" name="ownership_type" rules={[{ required: true, message: '请选择归属类型' }]}>
            <Select
              options={[
                { label: '仓库所属', value: 'warehouse' },
                { label: '客户所属', value: 'customer' },
              ]}
            />
          </Form.Item>

          {ownershipVal === 'warehouse' ? (
            <>
              <Form.Item label="所属仓库" name="warehouse" rules={[{ required: true, message: '请选择仓库' }]}>
                <Select
                  showSearch
                  loading={optsLoading}
                  options={warehouseOptions}
                  placeholder="选择仓库"
                  filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
              <Form.Item label="价格($)" name="price" rules={[{ required: true, message: '请输入价格（可为0）' }]}>
                <InputNumber min={0} precision={2} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </>
          ) : (
            <Form.Item label="所属客户" name="customer" rules={[{ required: true, message: '请选择客户' }]}>
              <Select
                showSearch
                loading={optsLoading}
                options={customerOptions}
                placeholder="选择客户"
                filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          )}

          <Form.Item label="包材类型" name="packaging_type" rules={[{ required: true, message: '请选择包材类型' }]}>
            <Select
              options={[
                { label: typeLabelFull('pallet'), value: 'pallet' },
                { label: typeLabelFull('carton'), value: 'carton' },
                { label: typeLabelFull('shipping_bag'), value: 'shipping_bag' },
                { label: typeLabelFull('bubble_mailer'), value: 'bubble_mailer' },
                { label: typeLabelFull('other'), value: 'other' },
              ]}
            />
          </Form.Item>

          <Form.Item label="包材新旧" name="condition" rules={[{ required: true, message: '请选择新旧' }]}>
            <Select options={[{ label: '全新', value: 'new' }, { label: '旧', value: 'used' }]} />
          </Form.Item>
          <Form.Item label="是否可用" name="is_active" initialValue={true}>
            <Select options={[{ label: '可用', value: true }, { label: '不可用', value: false }]} />
          </Form.Item>

          <Form.Item label="包材名称（可选）" name="name">
            <Input maxLength={200} placeholder="可不填" />
          </Form.Item>

          <Space style={{ width: '100%' }} size={12}>
            <Form.Item label="长(cm)" name="length_cm" style={{ flex: 1 }}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="宽(cm)" name="width_cm" style={{ flex: 1 }}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="高(cm)" name="height_cm" style={{ flex: 1 }}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item label="重量(g)" name="weight_g">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="备注（可选）" name="remarks">
            <Input.TextArea rows={3} maxLength={1000} placeholder="可不填" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 打印：包材标签预览（后端生成PDF） */}
      <Modal
        title={labelRow ? `包材标签打印预览 - ${labelRow.system_code || labelRow.id}` : '包材标签打印预览'}
        open={labelOpen}
        onCancel={() => { setLabelOpen(false); setLabelRow(null); }}
        width={900}
        okText="打印"
        cancelText="关闭"
        onOk={() => {
          try {
            const cw = labelIframeRef.current?.contentWindow;
            if (!cw) { message.warning('预览未就绪'); return; }
            cw.focus();
            cw.print();
          } catch {
            message.error('打印失败');
          }
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Space style={{ width:'100%', justifyContent:'space-between' }}>
            <Button
              onClick={async () => {
                try {
                  if (!labelRow?.id) return;
                  const resp = await downloadShippingPackagingLabel(Number(labelRow.id));
                  const blob = resp?.data as Blob;
                  const href = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = href;
                  a.download = `shipping_packaging_label_${labelRow.system_code || labelRow.id}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(() => { try { URL.revokeObjectURL(href); } catch {} }, 500);
                } catch (e:any) {
                  message.error(e?.response?.data?.error || '下载失败');
                }
              }}
            >
              下载PDF
            </Button>
            <Space>
              <CancelBtn />
              <OkBtn />
            </Space>
          </Space>
        )}
      >
        {labelLoading ? (
          <div style={{ color:'#999' }}>生成中...</div>
        ) : labelUrl ? (
          <div style={{ height: '70vh' }}>
            <iframe
              ref={labelIframeRef as any}
              title="shipping-packaging-label-preview"
              src={labelUrl}
              style={{ width: '100%', height: '100%', border: 0 }}
            />
          </div>
        ) : (
          <div style={{ color:'#999' }}>暂无预览</div>
        )}
      </Modal>
    </div>
  );
};

export default ShippingPackagingListPage;


